/**
 * Unified Live Pulse Ticker — portable copy for CaribNavi & CheepSweepz.
 */
import { useCallback, useEffect, useState } from 'react';
import directoryConfig from './mansion-directory.json';
import { sanitizeFinancialLabel, sanitizePublicCopy } from '../lib/public-copy';

const HQ_DEFAULT = 'https://digitalhustlerscaribbean.com';
const MANSION_FETCH_HEADERS = {
  Accept: 'application/json',
  'X-DHC-Hub': 'digitalhustlerscaribbean.com',
};

const OFFLINE_FALLBACK = directoryConfig.offlineLines;
const SITE_PROFILES = directoryConfig.siteProfiles;

function resolveSiteRole(raw) {
  const value = String(raw ?? 'dhc').toLowerCase();
  if (value === 'cheepsweepz' || value === 'cheep') return 'cheepsweepz';
  if (value === 'navi' || value === 'caribnavi') return 'navi';
  return 'dhc';
}

function getSiteProfile(site) {
  return SITE_PROFILES[site] ?? SITE_PROFILES.dhc;
}

function filterDirectoryForSite(entries, site) {
  const profile = getSiteProfile(site);
  const allowed = new Set(profile.directorySites);
  const filtered = entries.filter((entry) => allowed.has(entry.site) || entry.site === 'all');
  if (!profile.fallbackHandleFilter?.length) return filtered;
  const preferredHandles = new Set(profile.fallbackHandleFilter);
  const preferred = filtered.filter((entry) => preferredHandles.has(entry.handle));
  const rest = filtered.filter((entry) => !preferredHandles.has(entry.handle));
  return [...preferred, ...rest];
}

function resolveHqBaseUrl(explicit) {
  if (explicit) return explicit.replace(/\/$/, '');
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DHC_HQ_URL) {
    return import.meta.env.VITE_DHC_HQ_URL.replace(/\/$/, '');
  }
  if (typeof process !== 'undefined' && process.env?.REACT_APP_DHC_HQ_URL) {
    return process.env.REACT_APP_DHC_HQ_URL.replace(/\/$/, '');
  }
  return HQ_DEFAULT;
}

function formatFinancialHeartbeat(finData, site = 'dhc') {
  const profile = getSiteProfile(site);
  if (profile.financialLabel) {
    return profile.financialLabel;
  }

  if (site === 'cheepsweepz' || site === 'cheep') {
    return '✨ All-Inclusive Air Freight Gateway — Fully Cleared to Your Door';
  }

  if (site === 'navi') {
    return '✨ Unified Caribbean Network — Trusted Island Guidance';
  }

  return '✨ All-Inclusive Delivered Price — Premium Caribbean Concierge';
}

function dedupePulseItems(items) {
  const seen = new Set();
  const deduped = [];

  for (const item of items) {
    const key = String(item.title || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  return deduped;
}

function TickerSep({ id }) {
  return (
    <span className="ticker-sep" aria-hidden="true" key={id}>
      {' '}
      •{' '}
    </span>
  );
}

function renderStreamRow({
  financialLabel,
  financialFallback,
  pulseLinks,
  conciergeStreams,
  directoryEntries,
  offlineLines,
  suffix,
}) {
  const showPulse = pulseLinks && pulseLinks.length > 0;
  const showConcierge = !showPulse && Array.isArray(conciergeStreams) && conciergeStreams.length > 0;
  const showDirectory = Array.isArray(directoryEntries) && directoryEntries.length > 0;
  const showOffline = Array.isArray(offlineLines) && offlineLines.length > 0;

  return (
    <>
      <span className="ticker-accent-chip" key={`fin-${suffix}`}>
        {sanitizeFinancialLabel(financialLabel, financialFallback)}
      </span>
      <TickerSep id={`sep-fin-${suffix}`} />
      {showPulse
        ? pulseLinks.map((item, index) => (
            <span key={`pulse-wrap-${item.id}-${suffix}`} className="ticker-content-item">
              {index > 0 ? <TickerSep id={`sep-pulse-${suffix}-${index}`} /> : null}
              <a
                href={item.articleUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="ticker-news-link"
              >
                {item.isLive ? '🔴 ' : ''}
                {item.title}
              </a>
            </span>
          ))
        : null}
      {showConcierge
        ? conciergeStreams.map((line, index) => (
            <span key={`concierge-${suffix}-${index}`} className="ticker-content-item">
              {index > 0 || showPulse ? <TickerSep id={`sep-con-${suffix}-${index}`} /> : null}
              <span className="ticker-stream-line">{sanitizePublicCopy(line)}</span>
            </span>
          ))
        : null}
      {showPulse && (showConcierge || showDirectory) ? (
        <TickerSep id={`sep-dir-start-${suffix}`} />
      ) : null}
      {showConcierge && showDirectory ? <TickerSep id={`sep-dir-start2-${suffix}`} /> : null}
      {showOffline
        ? offlineLines.map((text, idx) => (
            <span key={`offline-${suffix}-${idx}`} className="ticker-content-item">
              {idx > 0 && !showPulse && !showConcierge ? (
                <TickerSep id={`sep-offline-${suffix}-${idx}`} />
              ) : null}
              {text}
            </span>
          ))
        : showDirectory
          ? directoryEntries.map((entry, idx) => (
              <span key={`dir-${entry.handle}-${suffix}`} className="ticker-content-item">
                {idx > 0 || showPulse || showConcierge ? (
                  <TickerSep id={`sep-dir-${suffix}-${idx}`} />
                ) : null}
                <a
                  href={entry.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ticker-directory"
                >
                  {entry.handle} | {entry.label}
                </a>
              </span>
            ))
          : null}
      <TickerSep id={`sep-end-${suffix}`} />
    </>
  );
}

async function fetchTickerBundle(HQ, siteRole) {
  const site = resolveSiteRole(siteRole);
  const tickerRes = await fetch(`${HQ}/api/ticker?site=${site}`, {
    cache: 'no-store',
    mode: 'cors',
    headers: MANSION_FETCH_HEADERS,
  });

  if (tickerRes.ok) {
    const bundle = await tickerRes.json();
    if (bundle?.success) {
      const directoryEntries = Array.isArray(bundle.directoryEntries)
        ? bundle.directoryEntries
        : filterDirectoryForSite(directoryConfig.handles, site);

      return {
        financialLabel:
          getSiteProfile(site).financialLabel ??
          (typeof bundle.financialLabel === 'string'
            ? bundle.financialLabel
            : formatFinancialHeartbeat(bundle, site)),
        pulseLinks: dedupePulseItems(
          Array.isArray(bundle.pulseItems)
            ? bundle.pulseItems.map((item, index) => ({
                id: item.id || `pulse-${index}`,
                title: item.title || 'Update',
                articleUrl: item.articleUrl,
                isLive: Boolean(item.isLive),
              }))
            : []
        ),
        conciergeStreams: Array.isArray(bundle.conciergeStreams) ? bundle.conciergeStreams : [],
        directoryEntries,
        mode: bundle.mode || 'directory',
      };
    }
  }

  const [financialRes, pulseRes] = await Promise.all([
    site === 'cheepsweepz' || site === 'cheep'
      ? Promise.resolve(null)
      : fetch(`${HQ}/api/profit-cost`, {
          cache: 'no-store',
          mode: 'cors',
          headers: MANSION_FETCH_HEADERS,
        }),
    fetch(`${HQ}/api/pulse?mode=live&limit=15`, {
      cache: 'no-store',
      mode: 'cors',
      headers: MANSION_FETCH_HEADERS,
    }),
  ]);

  let financialLabel =
    getSiteProfile(site).financialLabel ??
    '✨ Financial Heartbeat: Synchronizing canonical data...';
  let pulseLinks = [];
  let mode = 'directory';
  const profile = getSiteProfile(site);

  if (financialRes?.ok) {
    financialLabel = formatFinancialHeartbeat(await financialRes.json(), site);
  }

  if (pulseRes.ok) {
    const pulseData = await pulseRes.json();
    if (pulseData.success && Array.isArray(pulseData.items) && pulseData.items.length > 0) {
      pulseLinks = dedupePulseItems(
        pulseData.items.map((item, index) => ({
          id: item.id || `pulse-${index}`,
          title: item.title || 'Update',
          articleUrl: item.articleUrl,
          isLive: Boolean(item.isLive),
        }))
      );
      mode = pulseLinks.length > 0 ? 'pulse' : 'directory';
    }
  }

  return {
    financialLabel,
    pulseLinks,
    conciergeStreams: profile.conciergeStreams ?? [],
    directoryEntries: filterDirectoryForSite(directoryConfig.handles, site),
    mode,
  };
}

export function LivePulseTicker({
  hqBaseUrl,
  siteRole = 'dhc',
  badgeLabel = 'Barbados Pulse',
  autoRefresh = true,
  refreshInterval = 60000,
}) {
  const HQ = resolveHqBaseUrl(hqBaseUrl);
  const site = resolveSiteRole(siteRole);
  const financialFallback = getSiteProfile(site).financialLabel;
  const [financialLabel, setFinancialLabel] = useState(
    sanitizeFinancialLabel(null, financialFallback)
  );
  const [pulseLinks, setPulseLinks] = useState([]);
  const [conciergeStreams, setConciergeStreams] = useState([]);
  const [directoryEntries, setDirectoryEntries] = useState(
    filterDirectoryForSite(directoryConfig.handles, site)
  );
  const [offlineLines, setOfflineLines] = useState(null);
  const [mode, setMode] = useState('loading');

  const hydrate = useCallback(async () => {
    try {
      const bundle = await fetchTickerBundle(HQ, site);
      setFinancialLabel(
        sanitizeFinancialLabel(bundle.financialLabel, getSiteProfile(site).financialLabel)
      );
      setPulseLinks(bundle.pulseLinks);
      setConciergeStreams(bundle.conciergeStreams);
      setDirectoryEntries(bundle.directoryEntries);
      setOfflineLines(null);
      setMode(bundle.mode);
    } catch {
      setPulseLinks([]);
      setConciergeStreams(getSiteProfile(site).conciergeStreams ?? []);
      setDirectoryEntries(filterDirectoryForSite(directoryConfig.handles, site));
      setOfflineLines(OFFLINE_FALLBACK);
      setMode('offline');
    }
  }, [HQ, site]);

  useEffect(() => {
    hydrate();
    if (!autoRefresh) return undefined;
    const id = window.setInterval(hydrate, refreshInterval);
    return () => window.clearInterval(id);
  }, [autoRefresh, refreshInterval, hydrate]);

  const rowProps = {
    financialLabel,
    financialFallback: getSiteProfile(site).financialLabel,
    pulseLinks: mode === 'pulse' ? pulseLinks : [],
    conciergeStreams: mode !== 'pulse' ? conciergeStreams : [],
    directoryEntries: mode === 'offline' ? null : directoryEntries,
    offlineLines: mode === 'offline' ? offlineLines || OFFLINE_FALLBACK : null,
  };

  return (
    <div className="live-pulse-ticker global-ticker-viewport" data-live-pulse-ticker>
      <div className="live-pulse-ticker__badge">
        <span className="live-pulse-ticker__dot" />
        <span>{badgeLabel}</span>
      </div>
      <div className="live-pulse-ticker__viewport">
        <div className="live-pulse-ticker__track animate-scroll-slow animate-ticker-stream">
          {renderStreamRow({ ...rowProps, suffix: 'a' })}
          {renderStreamRow({ ...rowProps, suffix: 'b' })}
        </div>
      </div>
    </div>
  );
}

export default LivePulseTicker;
